import { Form, Input, Row, Select, Col, Button, Tag } from "antd"
import React, { useState, useCallback, useEffect } from "react"
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload'
import { DeleteOutlined, HolderOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { SortableContainer, SortableElement } from "react-sortable-hoc"
import TextArea from "antd/lib/input/TextArea"
import { formatJson } from "@/utils/helper"
import { useSearchParams } from "react-router-dom"
import { useAppContext } from '@/components/AppContext'
import { A4B_APPS } from '@/utils/constants'
import MonetisationPujaApi from "@a4b/api/src/modules/Monetisation/Puja"

interface Props {
    fields: any
    cdnUrl: string
    preSignUrl: string
    add: () => void
    remove: (item: any) => void
    datatype: string
}
const { Option } = Select;

interface FormItemsProps {
    index: number
    field: any
    remove: (item: any) => void
    cdnUrl: string
    preSignUrl: string
    itemIndex: number
}
const FormItems = (props: FormItemsProps) => {
    const { itemIndex, field, remove, cdnUrl, preSignUrl } = props;
    const { networkInstance, countryState, app } = useAppContext();
    const [searchParams] = useSearchParams();
    const [storeList, setStoreList] = useState<any[]>([]);
    const [storeListLoading, setStoreListLoading] = useState(false);
    const form = Form.useFormInstance(); // Get form instance to manipulate fields

    const { monetisationPujaApi, monetisationAstroPujaApi } = networkInstance.clientWithHeaders
    const api: MonetisationPujaApi = (app && app === A4B_APPS.SRIMANDIR) ? monetisationPujaApi : monetisationAstroPujaApi


    const fetchStoreList = useCallback(async (storeIdSearch = '', titleSearch = '') => {
        setStoreListLoading(true)
        try {
            const response = await api.storesApi.getStoreList(
                { limit: 20, offset: 0 },
                countryState?.countryCode,
                true,
                searchParams.get("language_code") || "hi",
                'created_at',
                undefined,
                undefined,
                'pooja',
                storeIdSearch,
                titleSearch
            )

            if (response?.data) {
                setStoreList(response.data.data?.store_list || [])
            }
        } catch (error) {
            console.error('Failed to fetch store list:', error)
            setStoreList([])
        }
        setStoreListLoading(false)
    }, [api.storesApi, countryState?.countryCode, searchParams])

    useEffect(() => {
        fetchStoreList();
    }, [fetchStoreList]);

    /**
     * Handles resetting the media and thumbnail fields when the media type is changed.
     */
    const handleMediaTypeChange = () => {
        // NOTE: The path is based on the original `shouldUpdate` logic in your second file.
        const basePath = ['data', 'influencer_recommendation_banner', 'stores_list', itemIndex];
        form.setFieldValue([...basePath, 'media_list'], []);
        form.setFieldValue([...basePath, 'media_thumbnail'], []);
    };

    return (
        <div style={{
            border: '1px solid #e0e0e0',
            marginBottom: '16px',
            padding: '16px',
            width: '100%',
            position: 'relative',
            backgroundColor: 'white',
            cursor: 'grab',
            borderRadius: '8px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease',
            zIndex: 10000000,
        }}>
            <Row style={{
                width: '100%',
                position: 'relative',
                alignItems: 'flex-start'
            }}>
                {/* Delete button and drag handle on right */}
                <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <HolderOutlined style={{ color: '#999', cursor: 'grab' }} />
                    <Button
                        onClick={() => remove(field.name)}
                        type='text'
                        icon={<DeleteOutlined />}
                        size='middle'
                        danger
                    />
                </div>

                {/* Main content */}
                <div style={{ width: '100%', marginTop: '24px' }}>
                    {/* Tag Type Row */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                        <Col span={8}>
                            <Form.Item
                                label="Tag Type"
                                name={[field.name, 'tag_details', 'type']}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[{ required: true, message: 'Please select tag type!' }]}
                            >
                                <Select
                                    placeholder="Select tag type"
                                    style={{ width: '100%' }}
                                >
                                    <Option value="recently_added">Recently Added</Option>
                                    <Option value="popularity">Popularity</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Store and Media Selection Row */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '16px' }} align="bottom">
                        <Col span={15}>
                            <Form.Item
                                label="Store Selection"
                                name={[field.name, 'store_id']}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[{ required: true, message: 'Please select a store!' }]}
                            >
                                <Select
                                    placeholder="Select store"
                                    loading={storeListLoading}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.children?.toString() || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    onSearch={(value) => fetchStoreList(value)}
                                    style={{ width: '100%' }}
                                    allowClear
                                    onClear={() => fetchStoreList()}
                                >
                                    {storeList.map((store: any) => (
                                        <Option key={store?.id} value={store?.id}>
                                            <b>{store.title}</b> - {store?.id}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                label="Media type"
                                name={[field.name, 'media_type']}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                initialValue="image" // Default to image
                            >
                                <Select
                                    placeholder="Select media type"
                                    style={{ width: '100%' }}
                                    onChange={handleMediaTypeChange} // Reset fields on change
                                >
                                    <Option value="image">Image</Option>
                                    <Option value="video">Video</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Media Upload Section */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues?.data?.influencer_recommendation_banner?.stores_list?.[itemIndex]?.media_type !==
                            currentValues?.data?.influencer_recommendation_banner?.stores_list?.[itemIndex]?.media_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const mediaType = getFieldValue(['data', 'influencer_recommendation_banner', 'stores_list', itemIndex, 'media_type']);
                            const isVideo = mediaType === 'video';

                            return (
                                <Row gutter={[16, 16]}>
                                    <Col span={isVideo ? 12 : 24}>
                                        <Form.Item
                                            label={isVideo ? "Video" : "Images"}
                                            name={[field.name, 'media_list']}
                                            labelCol={{ span: 24 }}
                                            rules={[
                                                {
                                                    required: true,
                                                    validator: async (rule, value) => {
                                                        if (!value || value.length < 1) {
                                                            return Promise.reject(
                                                                isVideo ? 'Please upload a video' : 'Please upload at least one image'
                                                            );
                                                        }
                                                    },
                                                },
                                            ]}
                                            valuePropName="fileList"
                                        >
                                            <S3Upload
                                                cdnUrl={cdnUrl}
                                                preSignUrl={preSignUrl}
                                                min={1}
                                                max={isVideo ? 1 : 10}
                                                multiple={!isVideo}
                                                allowedFileExtension={isVideo ? [AllowedFormats.MP4, AllowedFormats.GIF] : [AllowedFormats?.PNG, AllowedFormats?.JPEG, AllowedFormats?.JPG, AllowedFormats?.WEBP]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    {isVideo && (
                                        <Col span={12}>
                                            <Form.Item
                                                label="Thumbnail"
                                                name={[field.name, 'media_thumbnail']}
                                                labelCol={{ span: 24 }}
                                                rules={[
                                                    {
                                                        required: true,
                                                        validator: async (rule, values) => {
                                                            if (!values || values.length < 1) {
                                                                return Promise.reject('Please upload a Thumbnail');
                                                            }
                                                        },
                                                    },
                                                ]}
                                                valuePropName="fileList"
                                            >
                                                <S3Upload
                                                    cdnUrl={cdnUrl}
                                                    preSignUrl={preSignUrl}
                                                    min={1}
                                                    max={1}
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            );
                        }}
                    </Form.Item>
                </div>
            </Row>
        </div>
    )
}

const SortableFormItems = SortableElement(FormItems);

const Banners: React.FC<Props> = ({ fields, add, remove, cdnUrl, preSignUrl, datatype }) => {
    return (
        <>
            <Row justify="space-between" style={{ width: '100%' }}>
                {fields && fields.map((field: any, index: number) => {
                    return (
                        <SortableFormItems
                            key={field.key} // Use field.key for stable keys
                            index={index}
                            itemIndex={index}
                            field={field}
                            remove={remove}
                            cdnUrl={cdnUrl}
                            preSignUrl={preSignUrl}
                        />
                    )
                })}
                <Col span={24} style={{ paddingTop: '10px' }}>
                    <Row justify='start'>
                        <Col span={4}>
                            <Button
                                onClick={() => {
                                    add()
                                }}
                                type='ghost'
                                icon={<PlusCircleOutlined />}
                                size='middle'
                            >
                                {datatype}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    )
}

export default SortableContainer(Banners)